import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Line } from "react-chartjs-2";
// Constants - Functions
import { graphicDataColor1 } from "../../../config/constants";
// Components
import { RangeDatesDiv, RangeDatesLabel, DateInputDiv, DateInput, RangeDatesSeparator } from "./styles";
import Graphic from "../graphic";

const ArmEnergyPerDate = ({ armEnergyDates }) => {
  const [filteredArmEnergyDates, setFilteredArmEnergyDates] = useState([]);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [t] = useTranslation("global");

  // Setting initial values for the date range
  useEffect(() => {
    setFilteredArmEnergyDates(armEnergyDates);
    const startDate = new Date(moment(armEnergyDates[0].date, "DD-MM-YYYY"));
    const endDate = new Date(moment(armEnergyDates.at(-1).date, "DD-MM-YYYY"));
    setDateRange({ start: startDate, end: endDate });
  }, [armEnergyDates]);

  // Filtering arm energy by a date range
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return setFilteredArmEnergyDates(armEnergyDates);

    const formattedStartDate = moment(dateRange.start);
    const formattedEndDate = moment(dateRange.end);

    if (!formattedStartDate.isValid() || !formattedEndDate.isValid()) return setFilteredArmEnergyDates(armEnergyDates);

    const filteredArmEnergyDatesByDateRange = armEnergyDates.filter((armEnergyDate) => {
      const formattedArmEnergyDate = moment(armEnergyDate.date, "DD-MM-YYYY");
      return formattedArmEnergyDate.isSameOrAfter(formattedStartDate) && formattedArmEnergyDate.isSameOrBefore(formattedEndDate);
    });

    setFilteredArmEnergyDates(filteredArmEnergyDatesByDateRange);
  }, [armEnergyDates, dateRange]);

  const generateChartData = () => {
    let rangeOfTotalDates = [];
    let currentDate = moment(dateRange.start);
    const endDate = moment(dateRange.end);

    while (currentDate.isSameOrBefore(endDate, "day")) {
      rangeOfTotalDates.push(currentDate.format("DD-MM-YYYY"));
      currentDate.add(1, "day");
    }

    const armEnergy = rangeOfTotalDates.map((date) => {
      const energyDateObj = filteredArmEnergyDates.find((filteredArmEnergyDate) => {
        const formattedDate = moment(date, "DD-MM-YYYY").startOf("day");
        const filteredDate = moment(filteredArmEnergyDate.date, "DD-MM-YYYY").startOf("day");
        return formattedDate.isSame(filteredDate);
      });

      return {
        y: energyDateObj ? energyDateObj.armEnergy : NaN,
        x: energyDateObj ? energyDateObj.date : date,
      };
    });

    return {
      labels: rangeOfTotalDates,
      datasets: [
        {
          label: t("wear_armEnergy"),
          data: armEnergy,
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
      ],
    };
  };

  return (
    <Graphic
      Title={t("wear_armEnergyPerDatePlot_title")}
      Type={Line}
      data={generateChartData()}
      axisY={`${t("wear_armEnergy")} (m / s²)`}
      axisX={t("wear_armEnergyPerDatePlot_axisX")}
      graphicDescription={t("wear_armEnergyPerDatePlot_information")}
    >
      <RangeDatesDiv>
        <RangeDatesLabel>{t("wear_armEnergyPerDatePlot_dateRangeLabel")}</RangeDatesLabel>
        <DateInputDiv>
          <DateInput
            selected={dateRange.start}
            onChange={(date) => setDateRange({ ...dateRange, start: date })}
            dateFormat={"dd/MM/yyyy"}
          />
        </DateInputDiv>
        <RangeDatesSeparator />
        <DateInputDiv>
          <DateInput selected={dateRange.end} onChange={(date) => setDateRange({ ...dateRange, end: date })} dateFormat={"dd/MM/yyyy"} />
        </DateInputDiv>
      </RangeDatesDiv>
    </Graphic>
  );
};

export default ArmEnergyPerDate;
